import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import gql from 'graphql-tag';
import React, {CSSProperties, ReactNode, useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {canRequestAccess, getArgForRequestAccess, requestAccess} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {ReviewLifecycleType, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import openAICheckerPanel from 'notebook/ai-tools/openAICheckerPanel';
import {ReviewBadgeContext} from 'notebook/reviews/ReviewBadgeContext';
import * as ReviewDAO from 'notebook/reviews/ReviewDAO';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {getReviewLifecycleActionTitle} from 'notebook/reviews/ReviewUtil';
import notifications, {GenericResponseJSON} from 'notifications';
import _RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import EntityStore from 'stores/EntityStore';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, NEAR_BLACK, WHITE} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth, OptionValue} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SectionHeader from 'ui-library/menus/SectionHeader';
import {OngoingTaskResponse} from 'util/TaskRunner';

import {ReviewableQuery, ReviewableQueryVariables} from './__generated/ReviewableQuery';
import {REVIEW_ACTION_TYPE_TO_DISPLAY_TEXT} from './review-lifecycles/ReviewLifecycleConstants';
import {runSendForRLCReviewModal} from './review-lifecycles/runSendForRLCReviewModal';
import {Review} from './ReviewConstants';
import ReviewLifecycleSummaryPanel, {
  EntityByStageId,
  IsTeamReassignableByStageId,
} from './ReviewLifecycleSummaryPanel';

type ReviewableOrInaccessibleGraphQL = ReviewableQuery['reviewable'];

type ReviewableGraphQL = ExcludeInaccessible<ReviewableOrInaccessibleGraphQL>;

type ReviewableEntryGraphQL = ExtractByTypename<ReviewableOrInaccessibleGraphQL, 'EntryNode'>;

export type PermissionsGraphQL = ReviewableGraphQL['permissions'];

type ReviewLifecycleGraphQL = ReviewableGraphQL['parentReviewLifecycles'][number];

type ReviewGraphQL = NonNullable<ReviewableGraphQL['review']>;

type ReviewerGraphQL = NonNullable<ReviewGraphQL['reviewers']>[number];

function isEntry(data?: {__typename?: string}): data is ReviewableEntryGraphQL {
  return data?.__typename === 'EntryNode';
}

export interface DefaultBadgeSpec {
  text?: string;
  icon?: BenchlingIconName;
  style?: CSSProperties;
}

export interface WithPerformanceMetadata {
  performanceMetadata?: {[key: string]: string}; // takes in performance metric metadata from caller
}

export interface ReviewBadgeProps extends WithPerformanceMetadata {
  className?: string;
  reviewableId: string;
  enableReviewLifecycle?: boolean;
  defaultBadgeSpec?: DefaultBadgeSpec;
  onReviewChange?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  /** Custom error rendering to show errors in modal. */
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
  getSendForReviewValidationError?: (reviewableId: string) => string | null;
}

export const RESEND_FOR_REVIEW_DROPDOWN_TEXT = 'Resend for review';
export const SEND_FOR_REVIEW_DROPDOWN_TEXT = 'Send for review';
export const CHECK_ENTRY_WITH_AI_DROPDOWN_TEXT = 'Check entry';

function getExistingReviewersByStage(review: ReviewGraphQL | null): EntityByStageId {
  return (
    review?.reviewers?.reduce((acc: EntityByStageId, reviewer: ReviewerGraphQL) => {
      acc[reviewer.reviewStage.id] = reviewer.user;
      return acc;
    }, {}) ?? {}
  );
}

function getExistingCanTeamReassignByStage(review: ReviewGraphQL | null): IsTeamReassignableByStageId {
  return (
    review?.reviewers?.reduce((acc: IsTeamReassignableByStageId, reviewer: ReviewerGraphQL) => {
      acc[reviewer.reviewStage.id] = reviewer.isTeamReassignable;
      return acc;
    }, {}) ?? {}
  );
}

export const REVIEWABLE_QUERY = gql`
  query ReviewableQuery($reviewableId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    reviewable(id: $reviewableId) {
      ... on ReviewableInterface {
        id
        review {
          ...ReviewLifecycleReviewSummaryGraphQL
          activeReviewLifecycleVersion {
            ... on ReviewLifecycleVersionNode {
              id
              terminalState
            }
          }
        }
        parentReviewLifecycles {
          id
          currentVersion {
            id
            lifecycleType
            reviewStages {
              id
              action
            }
          }
        }
        reviewSource {
          id
        }
      }
      ... on EntryNode {
        id
        hasReviewCriteria
        template {
          id
          templateCollection {
            id
          }
        }
      }
      ... on HasPermissionsInterface {
        permissions {
          admin
          appendable
          readable
          writable
          canAccept
          canReassignRlcReviewer
          canReject
          canRetract
          canSendForReview
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ReviewLifecycleReviewSummary').fragment}
`;

export enum ReviewBadgeStatus {
  IN_PROGRESS,
  IN_REVIEW,
  ACCEPTED,
  REJECTED,
  ACTION_REQUIRED,
}

export function getBadgeStatus(status?: ReviewStatus): ReviewBadgeStatus {
  switch (status) {
    case ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
    case ReviewStatus.ACCEPTED:
      return ReviewBadgeStatus.ACCEPTED;
    case ReviewStatus.NEEDS_REVIEW:
    case ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS:
      return ReviewBadgeStatus.IN_REVIEW;
    case ReviewStatus.REJECTED:
      return ReviewBadgeStatus.REJECTED;
    case ReviewStatus.ACTION_REQUIRED:
      return ReviewBadgeStatus.ACTION_REQUIRED;
    case ReviewStatus.RETRACTED:
    case null:
    default:
      return ReviewBadgeStatus.IN_PROGRESS;
  }
}

export interface StageEntryReviewBadgeProps {
  className?: string;
  review?: Review;
}

export function StageEntryReviewBadge({review, className}: StageEntryReviewBadgeProps): JSX.Element {
  return (
    <span
      className={classNames('reviewBadge', ReviewUtil.getStatusCSSClassFromReview(review?.status), className)}
    >
      <BenchlingIcon className="reviewBadge-icon" icon={ReviewUtil.getStatusIcon(review?.status)} />
      {ReviewUtil.getStatusTextFromReview(review?.status)}
    </span>
  );
}

function handleInaccessibleReviewable(reviewable: ReviewableOrInaccessibleGraphQL): void {
  const argForRequestAccess = getArgForRequestAccess(reviewable);
  if (argForRequestAccess) {
    if (canRequestAccess(argForRequestAccess)) {
      requestAccess(argForRequestAccess);
    } else {
      notifications.error(FileBrowserConstants.INACCESSIBLE_MESSAGE);
    }
  }
}

function getSendForReviewDropdownText(
  reviewLifecycle: ReviewLifecycleGraphQL,
  review: ReviewGraphQL | null
): string {
  if (reviewLifecycle?.currentVersion.lifecycleType === ReviewLifecycleType.SELF_REVIEW) {
    return REVIEW_ACTION_TYPE_TO_DISPLAY_TEXT[reviewLifecycle.currentVersion.reviewStages[0].action];
  }

  return review != null ? RESEND_FOR_REVIEW_DROPDOWN_TEXT : SEND_FOR_REVIEW_DROPDOWN_TEXT;
}

function createOnSelectHandler({
  review,
  reviewLifecycle,
  sourceId,
  reviewableId,
  templateId,
  templateCollectionId,
  onReviewChange,
  renderReviewError,
  performanceMetadata,
}: {
  review: ReviewGraphQL | null;
  // TODO(BNCH-66037): Handle send for review option for multiple RLCs on project.
  reviewLifecycle: ReviewLifecycleGraphQL;
  sourceId: string;
  reviewableId: string;
  templateId: string | null;
  templateCollectionId: string | null;
  onReviewChange?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  /** Custom error rendering to show errors in modal. */
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
  performanceMetadata?: {[key: string]: string};
}): ((value?: OptionValue) => void) | null {
  return async () => {
    // Review Lifecycle should be non-null when it's a self-review option.
    if (reviewLifecycle.currentVersion.lifecycleType === ReviewLifecycleType.SELF_REVIEW) {
      const reviewLifecycleStageIds = reviewLifecycle.currentVersion.reviewStages.map((stage) => stage.id);
      const selfReviewAction = reviewLifecycle.currentVersion.reviewStages[0].action;
      const loadingMessage = `${getReviewLifecycleActionTitle(selfReviewAction)} Review`;
      try {
        LoadingScreen.show(loadingMessage);
        await performSelfReview(reviewableId, reviewLifecycleStageIds, onReviewChange);
      } finally {
        LoadingScreen.hide();
      }
    } else {
      runSendForRLCReviewModal({
        reviewableId,
        onRequestComplete: onReviewChange,
        existingReviewerUsersByStage: getExistingReviewersByStage(review),
        analyticsParams: {
          entry_id: reviewableId,
          reviewable_id: reviewableId,
          project_id: sourceId,
          template_id: templateId,
          template_collection_id: templateCollectionId,
        },
        existingCanTeamReassignByStage: getExistingCanTeamReassignByStage(review),
        renderReviewError,
        performanceMetadata,
      });
    }
  };
}

interface ReviewBadgeDropdownOptionDetails {
  text: string;
  isHeader?: boolean;
  onSelect: ((value?: OptionValue) => void) | null;
  dataTestAttrs?: DataTestAttrs;
}

function getSendForReviewDropdownOptions(
  reviewLifecycles: Array<ReviewLifecycleGraphQL>,
  reviewable: ReviewableGraphQL,
  onReviewChange?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void,
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null,
  performanceMetadata?: {[key: string]: string}
): Array<ReviewBadgeDropdownOptionDetails> {
  const options: Array<ReviewBadgeDropdownOptionDetails> = [];
  const parallelOrSequentialRLC = reviewLifecycles.find(({currentVersion: {lifecycleType}}) =>
    [ReviewLifecycleType.SEQUENTIAL, ReviewLifecycleType.PARALLEL].includes(lifecycleType)
  );
  const selfReviewRlcs = reviewLifecycles.filter(
    ({currentVersion: {lifecycleType}}) => lifecycleType === ReviewLifecycleType.SELF_REVIEW
  );

  if (parallelOrSequentialRLC != null) {
    options.push({
      text: getSendForReviewDropdownText(parallelOrSequentialRLC, reviewable.review),
      onSelect: createOnSelectHandler({
        review: reviewable.review,
        // TODO(BNCH-66037): If there is more than 1 parallel/sequential RLC, this is technically incorrect.
        reviewLifecycle: parallelOrSequentialRLC,
        reviewableId: reviewable.id,
        sourceId: reviewable.reviewSource.id,
        templateId: isEntry(reviewable) ? (reviewable.template?.id ?? null) : null,
        templateCollectionId: isEntry(reviewable)
          ? (reviewable.template?.templateCollection.id ?? null)
          : null,
        onReviewChange,
        renderReviewError,
        performanceMetadata,
      }),
      dataTestAttrs: {
        component: 'ReviewInProgressButtonOption',
        element: 'root',
        key: 'sendForReview',
      },
    });
  }
  if (selfReviewRlcs.length > 0) {
    if (options.length > 0) {
      // Only render section header for Self-Review if there is already a send for review option in the dropdown.
      options.push({
        onSelect: null,
        text: 'Self-Review',
        isHeader: true,
      });
    }
    selfReviewRlcs.forEach((selfReviewRlc) => {
      options.push({
        text: getSendForReviewDropdownText(selfReviewRlc, reviewable.review),
        onSelect: createOnSelectHandler({
          review: reviewable.review,
          reviewLifecycle: selfReviewRlc,
          reviewableId: reviewable.id,
          sourceId: reviewable.reviewSource.id,
          templateId: isEntry(reviewable) ? (reviewable.template?.id ?? null) : null,
          templateCollectionId: isEntry(reviewable)
            ? (reviewable.template?.templateCollection.id ?? null)
            : null,
          onReviewChange,
          renderReviewError,
          performanceMetadata,
        }),
        dataTestAttrs: {
          component: 'ReviewInProgressButtonOption',
          element: 'root',
          key: 'selfReview',
        },
      });
    });
  }
  if (BenchlingConfig.ELN_ENABLE_AI_CHECKER && isEntry(reviewable)) {
    options.push({
      text: CHECK_ENTRY_WITH_AI_DROPDOWN_TEXT,
      onSelect: () => {
        openAICheckerPanel(reviewable.id, 'review-badge');
      },
    });
  }
  return options;
}

/**
 * Displays review status of a reviewable object.
 *
 * Currently handles two types of statuses depending on whether the status is associated with a review lifecycle.
 *
 * For accepted, rejected, and in review reviewables, if the associated review is linked to a review lifecycle,
 * show a clickable button that drops down to render a ReviewLifecycleSummaryPanel. Otherwise, just show review status.
 *
 * For in progress reviewables, if the project has a review lifecycle and the user has send for review permissions,
 * display a clickable dropdown to allow them to send for review. Otherwise, just show review status.
 */
function ReviewBadge({
  reviewableId,
  enableReviewLifecycle,
  defaultBadgeSpec,
  onReviewChange,
  renderReviewError,
  getSendForReviewValidationError,
  performanceMetadata,
}: ReviewBadgeProps): JSX.Element | null {
  const result = useBenchlingQuery<ReviewableQuery, ReviewableQueryVariables>(REVIEWABLE_QUERY, {
    variables: {reviewableId},
    fetchPolicy: 'cache-and-network',
  });

  const refetch = result.refetch;
  const refetchCallback = useCallback(async (): Promise<void> => {
    await refetch({reviewableId});
  }, [refetch, reviewableId]);

  if (result.type === QueryResultType.PENDING) {
    return (
      <div className={css(reviewStyles.throbber)}>
        <HorizontalThrobber />
      </div>
    );
  } else if (result.type === QueryResultType.ERROR) {
    return null;
  }

  const {reviewable} = result.data;
  if (isInaccessible(reviewable)) {
    handleInaccessibleReviewable(reviewable);
    return null;
  }

  const hasReviewCriteria = isEntry(reviewable) ? reviewable.hasReviewCriteria : false;
  const {review, permissions} = reviewable;

  const reviewableReviewUsesReviewLifecycle = review?.reviewLifecycle != null;
  const projectReviewLifecycles = reviewable.parentReviewLifecycles;

  const badgeStatus = getBadgeStatus(review?.status);

  // The type predicate is helpful for convincing TS that projectReviewLifecycle is not null if
  // showSendForReview is true.
  const showSendForReview = ((projectReviewLifecycles: Array<ReviewLifecycleGraphQL>): boolean => {
    if (
      !enableReviewLifecycle ||
      !permissions.canSendForReview ||
      projectReviewLifecycles.length === 0 ||
      !ReviewUtil.canSendForReviewFromStatus(review?.status)
    ) {
      return false;
    }

    if (review?.status === ReviewStatus.REJECTED) {
      // Only show send for review for rejected reviewables that were rejected using legacy review process.
      // If it was rejected using a review lifecycle, we should show the summary panel.
      return !reviewableReviewUsesReviewLifecycle;
    }
    return true;
  })(projectReviewLifecycles);

  const showSummaryPanel =
    !showSendForReview &&
    reviewableReviewUsesReviewLifecycle &&
    badgeStatus !== ReviewBadgeStatus.IN_PROGRESS;

  let button = (
    <ReviewActionButton
      mode={badgeStatus}
      text={ReviewUtil.getStatusTextFromReview(
        review?.status,
        review?.activeReviewLifecycleVersion?.terminalState,
        defaultBadgeSpec?.text
      )}
      isDropdown={showSendForReview || showSummaryPanel}
      icon={
        <BenchlingIcon
          className="reviewBadge-icon"
          icon={ReviewUtil.getStatusIcon(review?.status, defaultBadgeSpec?.icon)}
        />
      }
      defaultBadgeStyle={defaultBadgeSpec?.style}
    />
  );

  function getErrorForSendForReviewDropdownOptions(
    reviewLifecycles: Array<ReviewLifecycleGraphQL>,
    hasReviewCriteria: boolean
  ): ReactNode | null {
    if (
      reviewLifecycles.some(
        ({currentVersion: {lifecycleType}}) =>
          lifecycleType === ReviewLifecycleType.SELF_REVIEW && hasReviewCriteria
      )
    ) {
      return (
        <div>
          Please go to the <b>Review</b> tab and remove <b>all review criteria</b> before proceeding to
          self-review
        </div>
      );
    }
    return null;
  }

  const error = getErrorForSendForReviewDropdownOptions(projectReviewLifecycles, hasReviewCriteria);

  if (showSendForReview) {
    button = (
      <ReviewableInProgressButton
        reviewableId={reviewableId}
        options={getSendForReviewDropdownOptions(
          projectReviewLifecycles,
          reviewable,
          onReviewChange,
          renderReviewError,
          performanceMetadata
        )}
        sendForReviewError={error}
        getSendForReviewValidationError={getSendForReviewValidationError}
      >
        {button}
      </ReviewableInProgressButton>
    );
  } else if (showSummaryPanel) {
    // Render with the Review Lifecycle Summary dropdown if it is present
    button = (
      <ButtonWithReviewSummary
        reviewableId={reviewableId}
        permissions={permissions}
        reviewId={review.id}
        onReviewChange={onReviewChange}
        renderReviewError={renderReviewError}
        performanceMetadata={performanceMetadata}
      >
        {button}
      </ButtonWithReviewSummary>
    );
  }
  return (
    <RealtimeUpdateSubscriptionWrapper apiId={reviewableId} refetch={refetchCallback}>
      {button}
    </RealtimeUpdateSubscriptionWrapper>
  );
}

async function performSelfReview(
  reviewableId: string,
  reviewLifecycleStageIds: Array<string>,
  onRequestComplete?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void
): Promise<void> {
  assert(reviewLifecycleStageIds.length === 1, 'Self-review review lifecycle must have a single stage');
  const stageReviewers = [
    {
      stageId: reviewLifecycleStageIds[0],
      reviewerUserId: EntityStore.currentUser().id,
    },
  ];

  const {review, filePermissions, snapshotTask} = await ReviewDAO.sendForRLCReview({
    fileApiId: reviewableId,
    message: '',
    rlcStageReviewersData: stageReviewers,
  });
  onRequestComplete?.(reviewableId, review, filePermissions, snapshotTask);
}

function ReviewableInProgressButton({
  children,
  reviewableId,
  options,
  sendForReviewError,
  getSendForReviewValidationError,
}: {
  children: JSX.Element;
  reviewableId: string;
  options: Array<ReviewBadgeDropdownOptionDetails>;
  sendForReviewError?: ReactNode;
  getSendForReviewValidationError?: (reviewableId: string) => string | null;
}): JSX.Element {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [disableOptions, setDisableOptions] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState<string | null>(null);

  const onToggleDropdown = (isDropdownOpen: boolean): void => {
    setIsDropdownOpen(isDropdownOpen);

    if (isDropdownOpen) {
      const sendForReviewValidationError = getSendForReviewValidationError?.(reviewableId);
      const newOptionsDisabledState = sendForReviewValidationError != null;

      if (newOptionsDisabledState !== disableOptions) {
        setDisableOptions(newOptionsDisabledState);
      }
      if (tooltipTitle !== sendForReviewValidationError) {
        setTooltipTitle(sendForReviewValidationError ?? null);
      }
    }
  };

  const activeEditorLock = useSelector(
    (state: BenchlingState) => state.ui.documentEditorLockState[reviewableId] ?? null
  );
  const isEntryEditLocked = activeEditorLock && DocumentEditorLockUtil.isLockedInRealtime(activeEditorLock);

  return (
    <Dropdown
      isOpen={isDropdownOpen}
      onToggle={onToggleDropdown}
      menu={
        <Menu width={MenuWidth.FIT_CONTENT} className={css(reviewStyles.rightAlignedMenu)}>
          {options.map(({text, onSelect, dataTestAttrs, isHeader}, index) => {
            if (isHeader) {
              return (
                <SectionHeader
                  className={css(reviewStyles.sectionHeader, reviewStyles.dropdownOption)}
                  key={index}
                >
                  {text}
                </SectionHeader>
              );
            }
            return (
              <Option
                key={index}
                onSelect={onSelect}
                dataTestAttrs={dataTestAttrs}
                isDisabled={
                  isEntryEditLocked ||
                  disableOptions ||
                  (text === 'Self-review' ? Boolean(sendForReviewError) : false)
                }
                tooltipTitle={tooltipTitle}
              >
                <div
                  className={css(
                    ...[
                      reviewStyles.dropdownOption,
                      ...(sendForReviewError ? [reviewStyles.dropdownOptionWithInlineBanner] : []),
                    ]
                  )}
                >
                  {text}
                </div>
              </Option>
            );
          })}

          {sendForReviewError && (
            <Option>
              <div className={css(reviewStyles.inlineBannerWrapper)}>
                <InlineBanner bannerType={InlineBannerType.ERROR} supportingInfo={sendForReviewError} />
              </div>
            </Option>
          )}
        </Menu>
      }
    >
      {children}
    </Dropdown>
  );
}

export interface ButtonWithReviewSummaryProps extends WithPerformanceMetadata {
  reviewableId: string;
  permissions: PermissionsGraphQL;
  children: JSX.Element;
  reviewId: string;
  onReviewChange?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
}

function ButtonWithReviewSummary({
  reviewableId,
  permissions,
  children,
  reviewId,
  onReviewChange,
  renderReviewError,
  performanceMetadata,
}: ButtonWithReviewSummaryProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const setIsOpenWrapper = useMemo(() => ({setIsOpen}), [setIsOpen]);
  return (
    <Dropdown
      closeOnInsideClick={false}
      isOpen={isOpen}
      onToggle={setIsOpen}
      menu={
        <div className={css(reviewStyles.menu)}>
          <ReviewBadgeContext.Provider value={setIsOpenWrapper}>
            <ReviewLifecycleSummaryPanel
              reviewableId={reviewableId}
              permissions={permissions}
              reviewId={reviewId}
              onReviewChange={onReviewChange}
              renderReviewError={renderReviewError}
              performanceMetadata={performanceMetadata}
            />
          </ReviewBadgeContext.Provider>
        </div>
      }
    >
      {children}
    </Dropdown>
  );
}

function ReviewActionButton(props: {
  mode: ReviewBadgeStatus;
  text: string | null;
  isDropdown: boolean;
  icon: JSX.Element;
  onClick?: () => void;
  defaultBadgeStyle?: CSSProperties;
}): JSX.Element {
  // use default color if review status in progress, otherwise use ReviewBadge set colors in REVIEW_BUTTON_STYLE_FOR_STATUS
  const badgeStyle =
    props.mode === ReviewBadgeStatus.IN_PROGRESS && props.defaultBadgeStyle
      ? props.defaultBadgeStyle
      : REVIEW_BUTTON_STYLE_FOR_STATUS[props.mode];
  return (
    <Button
      className={css(buttonStyle.button, badgeStyle)}
      icon={props.icon}
      isDropdown={props.isDropdown}
      disabled={!props.isDropdown}
      text={props.text}
      buttonStyle={ButtonStyle.PRIMARY}
      size={ButtonSize.MEDIUM}
      onClick={props.onClick}
      dataTestAttrs={{component: 'ReviewActionButton', element: 'root'}}
    />
  );
}

// export for ReviewBadge-test
export const reviewStyles = StyleSheet.create({
  accepted: {
    backgroundColor: Color.GREEN_600,
    ':hover:not(.is-disabled)': {backgroundColor: Color.GREEN_550},
    ':active:not(.is-disabled)': {backgroundColor: Color.GREEN_500},
  },
  needsReview: {
    backgroundColor: Color.ORANGE_350,
    ':hover:not(.is-disabled)': {backgroundColor: Color.ORANGE_300},
    ':active:not(.is-disabled)': {backgroundColor: Color.ORANGE_200},
    color: Color.GRAY_900,
  },
  inProgress: {
    backgroundColor: Color.BLUE_500,
    ':hover:not(.is-disabled)': {backgroundColor: Color.BLUE_450},
    ':active:not(.is-disabled)': {backgroundColor: Color.BLUE_400},
  },
  redButton: {
    backgroundColor: Color.RED_500,
    ':hover:not(.is-disabled)': {backgroundColor: Color.RED_450},
    ':active:not(.is-disabled)': {backgroundColor: Color.RED_400},
  },
  rightAlignedMenu: {
    right: 0,
  },
  menu: {
    backgroundColor: WHITE,
    borderRadius: 4,
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
    marginTop: 3,
    padding: 14,
    position: 'absolute',
    zIndex: 1000, // Keep in sync with zindex-dropdown in `less/bootstrap/variables.less`
    minWidth: 200,
    top: '100%',
    overflow: 'scroll',
    right: 0,
  },
  throbber: {
    verticalAlign: 'middle',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 10,
  },
  dropdownOption: {
    marginLeft: -2,
    maxWidth: 180,
  },
  sectionHeader: {borderTop: `1px solid ${GRAY_LIGHT}`},
  dropdownOptionWithInlineBanner: {
    paddingBottom: 4,
    paddingTop: 4,
  },
  inlineBannerWrapper: {
    paddingTop: 4,
    color: NEAR_BLACK,
    maxWidth: '240px',
  },
});

export const buttonStyle = StyleSheet.create({
  button: {
    borderRadius: 16,
    fontSize: 14,
    padding: '8px 12px',
    fontWeight: 500,
    height: 32,
    color: WHITE,
    cursor: 'default',
    ':hover:not(.is-disabled)': {cursor: 'pointer'},
  },
});

export const REVIEW_BUTTON_STYLE_FOR_STATUS = {
  [ReviewBadgeStatus.ACCEPTED]: reviewStyles.accepted,
  [ReviewBadgeStatus.REJECTED]: reviewStyles.redButton,
  [ReviewBadgeStatus.IN_PROGRESS]: reviewStyles.inProgress,
  [ReviewBadgeStatus.IN_REVIEW]: reviewStyles.needsReview,
  [ReviewBadgeStatus.ACTION_REQUIRED]: reviewStyles.redButton,
};

export default React.memo(ReviewBadge);

let RealtimeUpdateSubscriptionWrapper = _RealtimeUpdateSubscriptionWrapper;

export function mock(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function MockRealtimeUpdateSubscriptionWrapper(props: any): null | JSX.Element {
    return props.children;
  }
  RealtimeUpdateSubscriptionWrapper = MockRealtimeUpdateSubscriptionWrapper;
}

export function restore(): void {
  RealtimeUpdateSubscriptionWrapper = _RealtimeUpdateSubscriptionWrapper;
}
